import React, { useState, useEffect } from 'react'
import { Card, Switch, Radio, Input, Typography, Space } from 'antd'
import editIcon from '../../asserts/icon/icon-edit.svg'
import { editClient } from '../../api'
import './styles/Community.css'

const { Text } = Typography

export default function Community({ user, loginRole }) {
  // Defensive checks for user and profile
  let person = undefined
  let flags = {}
  try {
    if (loginRole !== 'Expert') {
      person = user && user.profile ? user.profile : undefined
      flags = person && person.flags ? person.flags : {}
    } else {
      person = user
      flags = user && user.profile && user.profile.flags ? user.profile.flags : {}
    }
  } catch (e) {
    person = undefined
    flags = {}
  }

  const [joinCommunity, setJoinCommunity] = useState(
    typeof flags.isInCommunity === 'boolean' ? flags.isInCommunity : true
  )
  const [anonymous, setAnonymous] = useState(
    typeof flags.isAnnonymous === 'boolean' ? flags.isAnnonymous : false
  )
  const [aliasType, setAliasType] = useState('')
  const [customAlias, setCustomAlias] = useState('')
  const [systemAlias, setSystemAlias] = useState('')
  const [editingCustomAlias, setEditingCustomAlias] = useState(false)

  const generateAlias = () => {
    let idPart = ''
    if (person && person.id) {
      const idStr = String(person.id)
      idPart = idStr.slice(-4).padStart(4, '0')
    } else {
      idPart = String(Math.floor(1000 + Math.random() * 9000))
    }
    return `Anon${idPart}`
  }

  useEffect(() => {
    if (!person) {
      setSystemAlias('')
      setCustomAlias('')
      setAliasType('')
      return
    }
    let alias = ''
    try {
      alias = loginRole !== 'Expert'
        ? person.community_alias
        : person.profile && person.profile.community_alias
    } catch {
      alias = ''
    }
    if (alias && /^Anon\d{4}$/.test(alias)) {
      setSystemAlias(alias)
      setAliasType('system')
      setCustomAlias('')
    } else if (alias && alias.length > 0) {
      setCustomAlias(alias)
      setAliasType('custom')
      setSystemAlias('')
    } else {
      setSystemAlias('')
      setCustomAlias('')
      setAliasType('')
    }
  }, [person, loginRole])

  const updateClientData = async (person, joinVal = joinCommunity, anonVal = anonymous) => {
    if (!person || !person.id) return
    try {
      const updatedClientData = {
        profile: {
          flags: {
            isInCommunity: !!joinVal,
            isAnnonymous: !!anonVal
          }
        }
      }
      await editClient(updatedClientData, person.id)
    } catch (error) {
      console.error('Failed to update client flags:', error)
    }
  }

  const updateAlias = async (alias) => {
    if (!person || !person.id) return
    try {
      const updatedClientData = {
        profile: {
          community_alias: alias
        }
      }
      await editClient(updatedClientData, person.id)
    } catch (error) {
      console.error('Failed to update alias:', error)
    }
  }

  const handleJoinCommunityChange = (checked) => {
    setJoinCommunity(checked)
    if (person) updateClientData(person, checked, anonymous)
  }

  const handleAnonymousChange = (checked) => {
    setAnonymous(checked)
    if (person) updateClientData(person, joinCommunity, checked)
  }

  const handleAliasTypeChange = (value) => {
    setAliasType(value)
    if (value === 'system') {
      setEditingCustomAlias(false)
      setCustomAlias('')
      let alias = systemAlias
      if (!alias) {
        alias = generateAlias()
        setSystemAlias(alias)
      }
      if (person) updateAlias(alias)
    }
  }

  const handleEditCustomAlias = () => {
    setEditingCustomAlias(true)
  }

  const handleDoneCustomAlias = async () => {
    setEditingCustomAlias(false)
    if (customAlias && customAlias.length >= 3 && customAlias.length <= 20 && person) {
      await updateAlias(customAlias)
    }
  }

  return (
    <Card className="community-card">
      <div className="community-title">Community</div>
      <Space
        direction="vertical"
        size="large"
        className="community-space"
      >
        <div className="community-row">
          <div className="community-label">Join Community</div>
          <Switch
            checked={joinCommunity}
            onChange={handleJoinCommunityChange}
            className="community-switch"
            checkedChildren={null}
            unCheckedChildren={null}
          />
        </div>
        {joinCommunity && (
          <>
            <div className="community-row">
              <div className="community-label">Do you want to be anonymous?</div>
              <Switch
                checked={anonymous}
                onChange={handleAnonymousChange}
                className="community-switch"
                checkedChildren={null}
                unCheckedChildren={null}
              />
            </div>
            {anonymous && (
              <Radio.Group
                onChange={(e) => handleAliasTypeChange(e.target.value)}
                value={aliasType}
                className="community-radio-group"
              >
                <Space direction="vertical">
                  <Radio value="system" className="community-radio">
                    <div className="community-label">Use system-generated alias</div>
                    {aliasType === 'system' && (
                      <div>
                        <Text type="secondary">
                          Your alias name will be:{' '}
                          <Text code>
                            {systemAlias ||
                              (person &&
                                person.community_alias &&
                                /^Anon\d{4}$/.test(person.community_alias)
                                ? person.community_alias
                                : '')}
                          </Text>
                        </Text>
                      </div>
                    )}
                  </Radio>
                  {aliasType === 'system' && (
                    <Text type="secondary" className="community-alias-hint">
                      Your alias name will be:{' '}
                      <Text code>
                        {systemAlias ||
                          (person &&
                            person.community_alias &&
                            /^Anon\d{4}$/.test(person.community_alias)
                            ? person.community_alias
                            : '')}
                      </Text>
                    </Text>
                  )}
                  <Radio value="custom" className="community-radio">
                    <div>
                      <div className="community-label">
                        {customAlias
                          ? `Custom alias: ${customAlias}`
                          : 'Create custom alias'}
                      </div>
                      {aliasType === 'custom' && (
                        <div className="community-custom-alias-edit">
                          {!editingCustomAlias ? (
                            <Space>
                              <Text code>
                                {customAlias || 'No alias set'}
                              </Text>
                              <a onClick={handleEditCustomAlias} className="community-edit-link">
                                <img className="community-edit-icon" src={editIcon} alt="Edit" />
                              </a>
                            </Space>
                          ) : (
                            <Space>
                              <Input
                                className="community-custom-alias-input"
                                placeholder="Enter your custom alias"
                                value={customAlias}
                                onChange={(e) => setCustomAlias(e.target.value)}
                                maxLength={20}
                              />
                              <a onClick={handleDoneCustomAlias} className="community-done-link">
                                Done
                              </a>
                            </Space>
                          )}
                        </div>
                      )}
                    </div>
                  </Radio>
                </Space>
              </Radio.Group>
            )}
          </>
        )}
      </Space>
    </Card>
  )
}
